import {
  assertReportingDatasetRowsSize,
  assertReportingSnapshotSize,
  MAX_REPORTING_RESULT_BYTES,
  reportingDatasetRowsByteLength,
} from 'src/queries/reporting/reporting.snapshot.boundary'
import {
  REPORTING_AGENT_VERSION,
  type ReportingCell,
  type ReportingChartSpec,
  type ReportingColumn,
  type ReportingDataset,
} from 'src/queries/reportingAgent/reportingAgent.models'

const object = (value: unknown, name: string): Record<string, unknown> => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error(`${name} must be an object`)
  }
  return value as Record<string, unknown>
}

const exact = (
  value: Record<string, unknown>,
  keys: readonly string[],
  name: string,
): void => {
  const allowed = new Set(keys)
  const unknown = Object.keys(value).filter((key) => !allowed.has(key))
  if (unknown.length > 0)
    throw new Error(`${name} has unknown fields: ${unknown.join(', ')}`)
}

const string = (value: unknown, name: string): string => {
  if (typeof value !== 'string') throw new Error(`${name} must be a string`)
  return value
}

const nonEmptyString = (value: unknown, name: string): string => {
  const result = string(value, name)
  if (result.length === 0) throw new Error(`${name} must not be empty`)
  return result
}

const version = (value: unknown, name: string): void => {
  if (value !== REPORTING_AGENT_VERSION)
    throw new Error(`${name}.version is unsupported`)
}

const nullableString = (value: unknown, name: string): string | null =>
  value === null ? null : string(value, name)

const isoDate = (value: unknown, name: string): string => {
  const result = string(value, name)
  if (
    result.startsWith('0000-') ||
    !TIMESTAMP_PATTERN.test(result) ||
    Number.isNaN(Date.parse(result))
  )
    throw new Error(`${name} must be a canonical UTC timestamp`)
  const parsed = new Date(result)
  if (parsed.toISOString().slice(0, 19) !== result.slice(0, 19))
    throw new Error(`${name} must be a real UTC timestamp`)
  return result
}

const cell = (value: unknown, name: string): ReportingCell => {
  if (
    value !== null &&
    typeof value !== 'string' &&
    typeof value !== 'boolean'
  ) {
    throw new Error(`${name} must be a scalar cell`)
  }
  return value
}

const KEY_PATTERN = /^[a-z][a-z0-9_]{0,63}$/
const PHYSICAL_TYPES = new Set([
  'string',
  'int64',
  'decimal',
  'date',
  'timestamp',
  'boolean',
])
const SEMANTIC_TYPES = new Set([
  'category',
  'currency_amount',
  'currency_code',
  'number',
  'percentage',
  'date',
  'timestamp',
  'text',
  'boolean',
])
const UNITS = new Set(['minor_currency', 'major_currency', 'percent', 'count'])
const CURRENCIES = new Set([
  'AUD',
  'CAD',
  'CHF',
  'EUR',
  'GBP',
  'HKD',
  'JPY',
  'MXN',
  'NZD',
  'SGD',
  'USD',
])
const INT64_PATTERN = /^-?(0|[1-9][0-9]*)$/
const DECIMAL_PATTERN = /^-?(0|[1-9][0-9]*)(\.[0-9]+)?$/
const DATE_PATTERN = /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/
const TIMESTAMP_PATTERN =
  /^[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}(\.[0-9]{1,6})?Z$/
const SAFE_CHART_NUMBER = 9007199254740991
const CHART_FORMATS = new Set([
  'currency',
  'number',
  'integer',
  'percent',
  'date',
  'text',
])

const int64WithinBounds = (value: string): boolean => {
  const negative = value.startsWith('-')
  const digits = (negative ? value.slice(1) : value).replace(/^0+(?=\d)/, '')
  const limit = negative ? '9223372036854775808' : '9223372036854775807'
  return (
    digits.length < limit.length ||
    (digits.length === limit.length && digits <= limit)
  )
}

const normalizedDecimal = (value: string): string => {
  let expanded = value
  const exponentIndex = expanded.search(/[eE]/)
  if (exponentIndex >= 0) {
    const coefficient = expanded.slice(0, exponentIndex)
    const exponent = Number(expanded.slice(exponentIndex + 1))
    const negative = coefficient.startsWith('-')
    const unsignedCoefficient = negative ? coefficient.slice(1) : coefficient
    const digits = unsignedCoefficient.replace('.', '')
    const decimalIndex =
      unsignedCoefficient.length -
      (unsignedCoefficient.includes('.')
        ? unsignedCoefficient.length - unsignedCoefficient.indexOf('.') - 1
        : 0) +
      exponent
    if (decimalIndex <= 0)
      expanded = `${negative ? '-' : ''}0.${'0'.repeat(-decimalIndex)}${digits}`
    else if (decimalIndex >= digits.length)
      expanded = `${negative ? '-' : ''}${digits}${'0'.repeat(decimalIndex - digits.length)}`
    else
      expanded = `${negative ? '-' : ''}${digits.slice(0, decimalIndex)}.${digits.slice(decimalIndex)}`
  }
  const negative = expanded.startsWith('-')
  const unsigned = negative ? expanded.slice(1) : expanded
  const [whole, fraction = ''] = unsigned.split('.')
  const normalizedWhole = whole.replace(/^0+(?=\d)/, '')
  const normalizedFraction = fraction.replace(/0+$/, '')
  return `${negative ? '-' : ''}${normalizedWhole || '0'}${normalizedFraction ? `.${normalizedFraction}` : ''}`
}

const safelyRepresentableChartNumber = (value: string): boolean => {
  const numeric = Number(value)
  return (
    Number.isFinite(numeric) &&
    Math.abs(numeric) <= SAFE_CHART_NUMBER &&
    normalizedDecimal(value) === normalizedDecimal(numeric.toString())
  )
}

const validateColumn = (
  item: ReportingColumn,
  all: ReportingColumn[],
): void => {
  if (!KEY_PATTERN.test(item.key))
    throw new Error(`column key is invalid: ${item.key}`)
  if (item.label.length < 1 || item.label.length > 128)
    throw new Error(`column label is invalid: ${item.key}`)
  if (
    !PHYSICAL_TYPES.has(item.physical_type) ||
    !SEMANTIC_TYPES.has(item.semantic_type)
  )
    throw new Error(`column type is invalid: ${item.key}`)
  if (item.currency_field !== null && !KEY_PATTERN.test(item.currency_field))
    throw new Error(`column currency_field is invalid: ${item.key}`)
  if (item.unit !== null && !UNITS.has(item.unit))
    throw new Error(`column unit is invalid: ${item.key}`)
  if (
    item.semantic_type === 'currency_amount' &&
    (!['int64', 'decimal'].includes(item.physical_type) ||
      item.currency_field === null ||
      !['minor_currency', 'major_currency'].includes(item.unit ?? ''))
  )
    throw new Error(`currency_amount column is invalid: ${item.key}`)
  if (
    item.semantic_type === 'currency_code' &&
    (item.physical_type !== 'string' || item.currency_field !== null)
  )
    throw new Error(`currency_code column is invalid: ${item.key}`)
  if (
    ['category', 'text'].includes(item.semantic_type) &&
    item.physical_type !== 'string'
  )
    throw new Error(`semantic type is incompatible: ${item.key}`)
  if (
    item.semantic_type === 'percentage' &&
    (!['int64', 'decimal'].includes(item.physical_type) ||
      item.unit !== 'percent')
  )
    throw new Error(`percentage column is invalid: ${item.key}`)
  if (
    item.semantic_type === 'number' &&
    (!['int64', 'decimal'].includes(item.physical_type) ||
      ![null, 'count'].includes(item.unit))
  )
    throw new Error(`number column is invalid: ${item.key}`)
  if (item.semantic_type === 'date' && item.physical_type !== 'date')
    throw new Error(`date column is invalid: ${item.key}`)
  if (item.semantic_type === 'timestamp' && item.physical_type !== 'timestamp')
    throw new Error(`timestamp column is invalid: ${item.key}`)
  if (item.semantic_type === 'boolean' && item.physical_type !== 'boolean')
    throw new Error(`boolean column is invalid: ${item.key}`)
  if (item.semantic_type !== 'currency_amount' && item.currency_field !== null)
    throw new Error(
      `currency_field is only valid for currency_amount: ${item.key}`,
    )
  if (
    [
      'category',
      'currency_code',
      'date',
      'timestamp',
      'text',
      'boolean',
    ].includes(item.semantic_type) &&
    item.unit !== null
  )
    throw new Error(`unit is invalid for ${item.semantic_type}`)
  if (
    item.currency_field !== null &&
    !all.some(
      (column) =>
        column.key === item.currency_field &&
        column.physical_type === 'string' &&
        column.semantic_type === 'currency_code',
    )
  )
    throw new Error(`currency_field binding is invalid: ${item.key}`)
}

const validateTypedCell = (
  column: ReportingColumn,
  value: ReportingCell,
): void => {
  if (value === null) {
    if (!column.nullable) throw new Error(`${column.key} is not nullable`)
    return
  }
  if (column.physical_type === 'string') {
    if (typeof value !== 'string')
      throw new Error(`${column.key} must be a string`)
    if (column.semantic_type === 'currency_code' && !CURRENCIES.has(value))
      throw new Error(`${column.key} contains unsupported currency`)
  } else if (column.physical_type === 'boolean') {
    if (typeof value !== 'boolean')
      throw new Error(`${column.key} must be a boolean`)
  } else if (column.physical_type === 'int64') {
    if (
      typeof value !== 'string' ||
      !INT64_PATTERN.test(value) ||
      !int64WithinBounds(value)
    )
      throw new Error(`${column.key} must use canonical int64 grammar`)
  } else if (column.physical_type === 'decimal') {
    if (typeof value !== 'string' || !DECIMAL_PATTERN.test(value))
      throw new Error(`${column.key} must use canonical decimal grammar`)
  } else if (column.physical_type === 'date') {
    if (
      typeof value !== 'string' ||
      value.startsWith('0000-') ||
      !DATE_PATTERN.test(value) ||
      Number.isNaN(Date.parse(`${value}T00:00:00Z`)) ||
      new Date(`${value}T00:00:00Z`).toISOString().slice(0, 10) !== value
    )
      throw new Error(`${column.key} must be an ISO date string`)
  } else if (column.physical_type === 'timestamp') {
    if (
      typeof value !== 'string' ||
      value.startsWith('0000-') ||
      !TIMESTAMP_PATTERN.test(value) ||
      Number.isNaN(Date.parse(value)) ||
      new Date(value).toISOString().slice(0, 19) !== value.slice(0, 19)
    )
      throw new Error(`${column.key} must be an ISO timestamp string`)
  }
}

export const MAX_REPORTING_ROWS = 1000
export { MAX_REPORTING_RESULT_BYTES }

const column = (value: unknown, index: number): ReportingColumn => {
  const source = object(value, `dataset.columns[${index}]`)
  exact(
    source,
    [
      'key',
      'label',
      'physical_type',
      'semantic_type',
      'nullable',
      'currency_field',
      'unit',
    ],
    `dataset.columns[${index}]`,
  )
  return {
    key: nonEmptyString(source.key, `dataset.columns[${index}].key`),
    label: string(source.label, `dataset.columns[${index}].label`),
    physical_type: string(
      source.physical_type,
      `dataset.columns[${index}].physical_type`,
    ),
    semantic_type: string(
      source.semantic_type,
      `dataset.columns[${index}].semantic_type`,
    ),
    nullable:
      source.nullable === true || source.nullable === false
        ? source.nullable
        : (() => {
            throw new Error(
              `dataset.columns[${index}].nullable must be boolean`,
            )
          })(),
    currency_field: nullableString(
      source.currency_field,
      `dataset.columns[${index}].currency_field`,
    ),
    unit: nullableString(source.unit, `dataset.columns[${index}].unit`),
  }
}

export const parseReportingDataset = (value: unknown): ReportingDataset => {
  const source = object(value, 'dataset')
  exact(
    source,
    [
      'version',
      'dataset_id',
      'columns',
      'rows',
      'row_count',
      'complete',
      'generated_at',
    ],
    'dataset',
  )
  version(source.version, 'dataset')
  if (!Array.isArray(source.columns))
    throw new Error('dataset.columns must be an array')
  const columns = source.columns.map(column)
  if (columns.length === 0) throw new Error('dataset.columns must not be empty')
  if (new Set(columns.map((item) => item.key)).size !== columns.length)
    throw new Error('dataset columns must have unique keys')
  columns.forEach((item) => validateColumn(item, columns))
  if (!Array.isArray(source.rows))
    throw new Error('dataset.rows must be an array')
  const rows = source.rows.map((row, rowIndex) => {
    const rowObject = object(row, `dataset.rows[${rowIndex}]`)
    const keys = new Set(columns.map((item) => item.key))
    exact(rowObject, [...keys], `dataset.rows[${rowIndex}]`)
    for (const key of keys) {
      const value = cell(rowObject[key], `dataset.rows[${rowIndex}].${key}`)
      validateTypedCell(columns.find((item) => item.key === key)!, value)
    }
    return rowObject as Record<string, ReportingCell>
  })
  if (rows.length > MAX_REPORTING_ROWS)
    throw new Error(`dataset.rows exceeds ${MAX_REPORTING_ROWS} rows`)
  assertReportingDatasetRowsSize(rows)
  if (
    typeof source.row_count !== 'number' ||
    !Number.isInteger(source.row_count) ||
    source.row_count < 0
  ) {
    throw new Error('dataset.row_count must be a non-negative integer')
  }
  if (source.row_count !== rows.length)
    throw new Error('dataset.row_count does not match rows')
  if (source.complete !== true) throw new Error('dataset.complete must be true')
  return {
    version: REPORTING_AGENT_VERSION,
    dataset_id: nonEmptyString(source.dataset_id, 'dataset.dataset_id'),
    columns,
    rows,
    row_count: source.row_count,
    complete: source.complete,
    generated_at: isoDate(source.generated_at, 'dataset.generated_at'),
  }
}

export const parseReportingChartSpec = (
  value: unknown,
  datasetValue?: unknown,
): ReportingChartSpec => {
  const source = object(value, 'chart_spec')
  exact(
    source,
    [
      'version',
      'dataset_id',
      'kind',
      'title',
      'sort',
      'table',
      'encoding',
      'top_n_other',
    ],
    'chart_spec',
  )
  version(source.version, 'chart_spec')
  const kinds = [
    'kpi',
    'line',
    'horizontal_bar',
    'grouped_bar',
    'stacked_bar',
    'diverging_bar',
    'pie',
    'table_only',
  ]
  if (typeof source.kind !== 'string' || !kinds.includes(source.kind))
    throw new Error('chart_spec.kind is invalid')
  if (!Array.isArray(source.sort))
    throw new Error('chart_spec.sort must be an array')
  const sort = source.sort.map((item, index) => {
    const sortValue = object(item, `chart_spec.sort[${index}]`)
    exact(sortValue, ['field', 'direction'], `chart_spec.sort[${index}]`)
    if (
      sortValue.direction !== 'ascending' &&
      sortValue.direction !== 'descending'
    )
      throw new Error('chart_spec.sort.direction is invalid')
    const field = nonEmptyString(
      sortValue.field,
      `chart_spec.sort[${index}].field`,
    )
    if (!KEY_PATTERN.test(field))
      throw new Error(`chart_spec.sort[${index}].field is invalid`)
    return {
      field,
      direction: sortValue.direction as 'ascending' | 'descending',
    }
  })
  const tableValue = object(source.table, 'chart_spec.table')
  exact(
    tableValue,
    ['dataset_id', 'columns', 'formatting', 'default_expanded'],
    'chart_spec.table',
  )
  if (!Array.isArray(tableValue.columns))
    throw new Error('chart_spec.table.columns must be an array')
  const tableColumns = tableValue.columns.map((item, index) => {
    const field = nonEmptyString(item, `chart_spec.table.columns[${index}]`)
    if (!KEY_PATTERN.test(field))
      throw new Error(`chart_spec.table.columns[${index}] is invalid`)
    return field
  })
  if (
    tableColumns.length === 0 ||
    new Set(tableColumns).size !== tableColumns.length
  )
    throw new Error('chart_spec.table.columns must be unique and non-empty')
  const formattingValue = object(
    tableValue.formatting,
    'chart_spec.table.formatting',
  )
  const formatting: Record<string, string> = {}
  for (const [key, item] of Object.entries(formattingValue)) {
    formatting[key] = string(item, `chart_spec.table.formatting.${key}`)
    if (!CHART_FORMATS.has(formatting[key]))
      throw new Error(`chart_spec.table.formatting.${key} is invalid`)
  }
  if (typeof tableValue.default_expanded !== 'boolean')
    throw new Error('chart_spec.table.default_expanded must be boolean')
  const encodingSource = object(source.encoding, 'chart_spec.encoding')
  const encoding: Record<
    string,
    { field: string; format?: string | null } | null
  > = {}
  for (const [key, item] of Object.entries(encodingSource)) {
    if (item === null) {
      encoding[key] = null
      continue
    }
    const encodingValue = object(item, `chart_spec.encoding.${key}`)
    exact(encodingValue, ['field', 'format'], `chart_spec.encoding.${key}`)
    const field = nonEmptyString(
      encodingValue.field,
      `chart_spec.encoding.${key}.field`,
    )
    if (!KEY_PATTERN.test(field))
      throw new Error(`chart_spec.encoding.${key}.field is invalid`)
    encoding[key] = {
      field,
      ...(encodingValue.format === undefined
        ? {}
        : {
            format:
              encodingValue.format === null
                ? null
                : nonEmptyString(
                    encodingValue.format,
                    `chart_spec.encoding.${key}.format`,
                  ),
          }),
    }
    if (
      encoding[key]?.format !== undefined &&
      encoding[key]?.format !== null &&
      !CHART_FORMATS.has(encoding[key]?.format!)
    )
      throw new Error(`chart_spec.encoding.${key}.format is invalid`)
  }
  const kind = source.kind as string
  let topNOther: ReportingChartSpec['top_n_other']
  if (source.top_n_other !== undefined && source.top_n_other !== null) {
    const topNOtherValue = object(source.top_n_other, 'chart_spec.top_n_other')
    exact(topNOtherValue, ['limit', 'label'], 'chart_spec.top_n_other')
    if (
      !Number.isInteger(topNOtherValue.limit) ||
      (topNOtherValue.limit as number) < 1 ||
      (topNOtherValue.limit as number) > 25 ||
      topNOtherValue.label !== 'Other' ||
      (kind !== 'pie' && kind !== 'horizontal_bar')
    ) {
      throw new Error('chart_spec.top_n_other is invalid')
    }
    topNOther = {
      limit: topNOtherValue.limit as number,
      label: 'Other',
    }
  } else if (source.top_n_other === null) {
    topNOther = null
  }
  const requiredEncoding =
    kind === 'kpi'
      ? ['value']
      : kind === 'pie'
        ? ['category', 'value']
        : kind === 'table_only'
          ? []
          : ['x', 'y']
  const optionalEncoding =
    kind === 'kpi' || kind === 'pie' || kind === 'table_only' ? [] : ['series']
  const encodingKeys = Object.keys(encoding)
  if (
    encodingKeys.some(
      (key) =>
        !requiredEncoding.includes(key) && !optionalEncoding.includes(key),
    ) ||
    requiredEncoding.some((key) => !encodingKeys.includes(key)) ||
    requiredEncoding.some((key) => encoding[key] === null)
  )
    throw new Error('chart_spec.encoding slots are invalid')
  const chart = {
    version: REPORTING_AGENT_VERSION,
    dataset_id: nonEmptyString(source.dataset_id, 'chart_spec.dataset_id'),
    kind: source.kind as ReportingChartSpec['kind'],
    title: string(source.title, 'chart_spec.title'),
    sort,
    table: {
      dataset_id: nonEmptyString(
        tableValue.dataset_id,
        'chart_spec.table.dataset_id',
      ),
      columns: tableColumns,
      formatting,
      default_expanded: tableValue.default_expanded,
    },
    encoding: encoding as ReportingChartSpec['encoding'],
    ...(topNOther === undefined ? {} : { top_n_other: topNOther }),
  }
  if (chart.title.length < 1 || chart.title.length > 256)
    throw new Error('chart_spec.title must be between 1 and 256 characters')
  if (chart.table.dataset_id !== chart.dataset_id)
    throw new Error('chart_spec table dataset binding mismatch')
  if (datasetValue !== undefined) {
    const dataset = object(datasetValue, 'dataset')
    const datasetId = nonEmptyString(dataset.dataset_id, 'dataset.dataset_id')
    if (datasetId !== chart.dataset_id)
      throw new Error('chart_spec dataset binding mismatch')
    const columns = Array.isArray(dataset.columns)
      ? dataset.columns.map((item, index) =>
          nonEmptyString(
            object(item, `dataset.columns[${index}]`).key,
            `dataset.columns[${index}].key`,
          ),
        )
      : []
    const fields = new Set(columns)
    for (const field of sort.map((item) => item.field))
      if (!fields.has(field))
        throw new Error(
          `chart_spec sort field is not a dataset column: ${field}`,
        )
    for (const field of [
      ...tableColumns,
      ...Object.values(encoding)
        .filter(
          (item): item is { field: string; format?: string | null } =>
            item !== null,
        )
        .map((item) => item.field),
    ]) {
      if (!fields.has(field))
        throw new Error(`chart_spec field ${field} is not a dataset column`)
    }
    const columnValues = Array.isArray(dataset.columns)
      ? dataset.columns.map((item, index) =>
          object(item, `dataset.columns[${index}]`),
        )
      : []
    const byField = new Map(
      columnValues.map((item) => [String(item.key), item]),
    )
    const compatibleFormat = (
      field: string,
      format: string | null | undefined,
    ): void => {
      if (format === undefined || format === null) return
      const column = byField.get(field)
      const semantic = column?.semantic_type
      const physical = column?.physical_type
      const compatible =
        semantic === 'currency_amount'
          ? format === 'currency'
          : semantic === 'percentage'
            ? format === 'percent'
            : semantic === 'number'
              ? format === 'number' ||
                (format === 'integer' && physical === 'int64')
              : ['date', 'timestamp'].includes(String(semantic))
                ? format === 'date'
                : format === 'text'
      if (!compatible)
        throw new Error(`chart_spec format is incompatible with ${field}`)
    }
    const numeric = new Set(['currency_amount', 'number', 'percentage'])
    const categorical = new Set([
      'category',
      'text',
      'currency_code',
      'date',
      'timestamp',
    ])
    const requireSemantic = (slot: string, allowed: Set<string>): void => {
      const field = encoding[slot]?.field
      if (field && !allowed.has(String(byField.get(field)?.semantic_type)))
        throw new Error(
          `chart_spec encoding ${slot} is semantically incompatible`,
        )
    }
    if (kind === 'kpi') requireSemantic('value', numeric)
    if (['line', 'grouped_bar', 'stacked_bar'].includes(kind)) {
      requireSemantic('x', categorical)
      requireSemantic('y', numeric)
      if (encoding.series) requireSemantic('series', categorical)
    }
    if (['horizontal_bar', 'diverging_bar'].includes(kind)) {
      requireSemantic('x', numeric)
      requireSemantic('y', categorical)
    }
    if (kind === 'pie') {
      requireSemantic('category', categorical)
      requireSemantic('value', numeric)
    }
    for (const [key, format] of Object.entries(formatting)) {
      if (!tableColumns.includes(key))
        throw new Error('table formatting must target a visible dataset field')
      compatibleFormat(key, format)
    }
    for (const reference of Object.values(encoding))
      if (reference !== null)
        compatibleFormat(reference.field, reference.format)
    const referencedFields = [
      ...Object.values(encoding)
        .filter(
          (item): item is { field: string; format?: string | null } =>
            item !== null,
        )
        .map((item) => item.field),
    ]
    const rows = Array.isArray(dataset.rows) ? dataset.rows : []
    for (const field of referencedFields) {
      if (!numeric.has(String(byField.get(field)?.semantic_type))) continue
      for (const row of rows) {
        const value = object(row, 'dataset row')[field]
        if (value === null || typeof value !== 'string') continue
        if (!safelyRepresentableChartNumber(value))
          throw new Error(`chart field is not safely representable: ${field}`)
      }
    }
  }
  return chart
}

export interface ReportingSnapshot {
  chart_spec: ReportingChartSpec | null
  dataset: ReportingDataset
}

/**
 * The one result-size boundary shared by chat snapshots and live results.
 * The byte count intentionally covers the exact canonical wire pair, not the
 * independently parsed objects or rows alone.
 */
export const parseReportingSnapshot = (
  chartSpecValue: unknown,
  datasetValue: unknown,
): ReportingSnapshot => {
  assertReportingSnapshotSize(chartSpecValue, datasetValue)
  const dataset = parseReportingDataset(datasetValue)
  const chart_spec =
    chartSpecValue === null
      ? null
      : parseReportingChartSpec(chartSpecValue, dataset)
  return { chart_spec, dataset }
}

const SHA256_PATTERN = /^sha256:[0-9a-f]{64}$/

/**
 * Parses the integrity-bearing dataset retained with a conversational chart
 * anchor, then returns only the customer-visible snapshot used by renderers.
 */
export const parseReportingAnalysisSnapshot = (
  chartSpecValue: unknown,
  datasetValue: unknown,
): ReportingSnapshot => {
  assertReportingSnapshotSize(chartSpecValue, datasetValue)
  const source = object(datasetValue, 'dataset')
  exact(
    source,
    [
      'version',
      'dataset_id',
      'columns',
      'rows',
      'row_count',
      'byte_size',
      'complete',
      'generated_at',
      'provenance',
    ],
    'dataset',
  )
  if (
    typeof source.byte_size !== 'number' ||
    !Number.isSafeInteger(source.byte_size) ||
    source.byte_size < 0
  ) {
    throw new Error('dataset.byte_size must be a non-negative safe integer')
  }
  if (source.byte_size !== reportingDatasetRowsByteLength(source.rows)) {
    throw new Error('dataset.byte_size does not match canonical rows')
  }
  const provenance = object(source.provenance, 'dataset.provenance')
  exact(
    provenance,
    [
      'run_id',
      'query_attempt_id',
      'clickhouse_query_id',
      'semantic_model_version',
      'semantic_manifest_hash',
      'query_spec_hash',
    ],
    'dataset.provenance',
  )
  ;[
    'run_id',
    'query_attempt_id',
    'clickhouse_query_id',
    'semantic_model_version',
  ].forEach((field) =>
    nonEmptyString(provenance[field], `dataset.provenance.${field}`),
  )
  ;['semantic_manifest_hash', 'query_spec_hash'].forEach((field) => {
    const hash = string(provenance[field], `dataset.provenance.${field}`)
    if (!SHA256_PATTERN.test(hash)) {
      throw new Error(`dataset.provenance.${field} must be a lowercase sha256`)
    }
  })

  const publicDataset = {
    version: source.version,
    dataset_id: source.dataset_id,
    columns: source.columns,
    rows: source.rows,
    row_count: source.row_count,
    complete: source.complete,
    generated_at: source.generated_at,
  }
  const dataset = parseReportingDataset(publicDataset)
  const chart_spec =
    chartSpecValue === null
      ? null
      : parseReportingChartSpec(chartSpecValue, dataset)
  return { chart_spec, dataset }
}
